import { normalizeImportCategory } from './importLogsParsingClassification';
import {
  isGenericSemanticTitle,
  resolveCanonicalEntryText,
  resolveCanonicalOriginalText,
  resolveCanonicalSourceText,
} from './importLogsSemanticText';
import { parseDateForStorage } from './importLogsParsingTime';
import { resolveDominantCategoryOverlaps } from './importLogsDominantCategoryResolution';

export const normalizeImportedLongNoteReviewRow = (row = {}, {
  parserMode = 'long_note_email',
  sourceMode = 'import_long_note',
  inputMode = 'import',
  rawText = '',
} = {}) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const canonicalText = resolveCanonicalEntryText(row);
  const canonicalTitle = String(row?.title || row?.titlePrefix || '').trim();
  const canonicalCategory = normalizeImportCategory(row?.category || 'log');
  const canonicalDate = row?.date instanceof Date
    ? new Date(row.date)
    : (row?.date ? parseDateForStorage(row.date) : null);
  const canonicalDateValid = canonicalDate instanceof Date && !Number.isNaN(canonicalDate.getTime());
  const canonicalEntryDate = row?.entryDate
    || (canonicalDateValid ? canonicalDate.toDateString() : null)
    || null;
  const canonicalSourceText = resolveCanonicalSourceText(row, rawText, canonicalText);

  return {
    ...row,
    category: canonicalCategory,
    title: canonicalTitle || row?.title || '',
    titlePrefix: String(row?.titlePrefix || row?.title || '').trim(),
    body: canonicalText,
    note: canonicalText,
    text: canonicalText,
    content: canonicalText,
    details: canonicalText,
    description: canonicalText,
    summary: canonicalText,
    value: canonicalText,
    date: canonicalDateValid ? canonicalDate : row?.date || null,
    entryDate: canonicalEntryDate,
    localTime: row?.localTime || null,
    timeSource: row?.timeSource || 'missing',
    timeBucket: row?.timeBucket || '',
    timeInferred: Boolean(row?.timeInferred),
    timeInferenceReason: row?.timeInferenceReason || row?.timeBucket || '',
    childName: row?.childName || null,
    importance: row?.importance || 'normal',
    include: row?.include !== false,
    parsedObservation: Boolean(row?.parsedObservation),
    parsedSourceCategory: row?.parsedSourceCategory || canonicalCategory,
    parsedSourceConfidence: row?.parsedSourceConfidence || row?.reviewConfidence || 'high',
    reviewConfidence: row?.reviewConfidence || 'high',
    reviewConfidenceLabel: row?.reviewConfidenceLabel || '',
    reviewConfidenceReasons: Array.isArray(row?.reviewConfidenceReasons) ? row.reviewConfidenceReasons : [],
    confidence: row?.confidence || row?.reviewConfidence || 'high',
    sourceText: canonicalSourceText,
    originalText: resolveCanonicalOriginalText(row, rawText, canonicalText),
    source: row?.source || 'import_long_note',
    mode: row?.mode || inputMode,
    parserMode: row?.parserMode || parserMode,
    sourceMode: row?.sourceMode || sourceMode,
    inputMode: row?.inputMode || inputMode,
    canonicalRowVersion: row?.canonicalRowVersion || 1,
    rawText: row?.rawText || rawText || canonicalSourceText || canonicalText || '',
    rawSourceText: row?.rawSourceText || rawText || canonicalSourceText || canonicalText || '',
    metadata: {
      ...(row.metadata || {}),
      canonicalRowVersion: row?.canonicalRowVersion || 1,
      parserMode: row?.parserMode || parserMode,
      sourceMode: row?.sourceMode || sourceMode,
      inputMode: row?.inputMode || inputMode,
      rawText: row?.rawText || rawText || canonicalSourceText || canonicalText || '',
      rawSourceText: row?.rawSourceText || rawText || canonicalSourceText || canonicalText || '',
    },
  };
};

export const normalizeImportedLongNoteReviewRows = (rows = [], options = {}) => (
  Array.isArray(rows)
    ? resolveDominantCategoryOverlaps(rows.map((row) => normalizeImportedLongNoteReviewRow(row, options)))
    : []
);

export const normalizeImportedSchoolReviewRow = (row = {}, {
  parserMode = 'school_text',
  sourceMode = 'school_text',
  inputMode = 'import',
  rawText = '',
} = {}) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const canonicalText = resolveCanonicalEntryText(row);
  const canonicalCategory = normalizeImportCategory(row?.category || 'log');
  const canonicalTitle = String(row?.title || row?.titlePrefix || '').trim();
  const canonicalTitlePrefix = String(row?.titlePrefix || row?.title || '').trim();
  const resolvedTitle = isGenericSemanticTitle(canonicalTitle, canonicalCategory) ? '' : canonicalTitle;
  const resolvedTitlePrefix = isGenericSemanticTitle(canonicalTitlePrefix, canonicalCategory) ? '' : canonicalTitlePrefix;
  const canonicalDate = row?.date instanceof Date
    ? new Date(row.date)
    : (row?.date ? parseDateForStorage(row.date) : null);
  const canonicalDateValid = canonicalDate instanceof Date && !Number.isNaN(canonicalDate.getTime());
  const canonicalEntryDate = row?.entryDate
    || (canonicalDateValid ? canonicalDate.toDateString() : null)
    || null;
  const canonicalSourceText = resolveCanonicalSourceText(row, rawText, canonicalText);

  return {
    ...row,
    category: canonicalCategory,
    title: resolvedTitle || row?.title || '',
    titlePrefix: '',
    body: canonicalText,
    note: canonicalText,
    text: canonicalText,
    content: canonicalText,
    details: canonicalText,
    description: canonicalText,
    summary: canonicalText,
    value: canonicalText,
    date: canonicalDateValid ? canonicalDate : row?.date || null,
    entryDate: canonicalEntryDate,
    localTime: row?.localTime || null,
    timeSource: row?.timeSource || 'missing',
    timeBucket: row?.timeBucket || '',
    timeInferred: Boolean(row?.timeInferred),
    timeInferenceReason: row?.timeInferenceReason || row?.timeBucket || '',
    childName: row?.childName || null,
    importance: row?.importance || 'normal',
    include: row?.include !== false,
    parsedObservation: Boolean(row?.parsedObservation),
    parsedSourceCategory: row?.parsedSourceCategory || canonicalCategory,
    parsedSourceConfidence: row?.parsedSourceConfidence || row?.reviewConfidence || 'high',
    reviewConfidence: row?.reviewConfidence || 'high',
    reviewConfidenceLabel: row?.reviewConfidenceLabel || '',
    reviewConfidenceReasons: Array.isArray(row?.reviewConfidenceReasons) ? row.reviewConfidenceReasons : [],
    confidence: row?.confidence || row?.reviewConfidence || 'high',
    sourceText: canonicalSourceText,
    originalText: resolveCanonicalOriginalText(row, rawText, canonicalText),
    source: row?.source || 'school_import',
    mode: row?.mode || inputMode,
    parserMode: row?.parserMode || parserMode,
    sourceMode: row?.sourceMode || sourceMode,
    inputMode: row?.inputMode || inputMode,
    canonicalRowVersion: row?.canonicalRowVersion || 1,
    rawText: row?.rawText || rawText || canonicalSourceText || canonicalText || '',
    rawSourceText: row?.rawSourceText || rawText || canonicalSourceText || canonicalText || '',
    metadata: {
      ...(row.metadata || {}),
      canonicalRowVersion: row?.canonicalRowVersion || 1,
      parserMode: row?.parserMode || parserMode,
      sourceMode: row?.sourceMode || sourceMode,
      inputMode: row?.inputMode || inputMode,
      rawText: row?.rawText || rawText || canonicalSourceText || canonicalText || '',
      rawSourceText: row?.rawSourceText || rawText || canonicalSourceText || canonicalText || '',
      explicitTitlePrefix: resolvedTitlePrefix,
    },
  };
};

export const normalizeImportedSchoolReviewRows = (rows = [], options = {}) => (
  Array.isArray(rows)
    ? resolveDominantCategoryOverlaps(rows.map((row) => normalizeImportedSchoolReviewRow(row, options)))
    : []
);

export const normalizeImportedFileReviewRow = (row = {}, {
  parserMode = 'file_import',
  sourceMode = 'docx_xlsx',
  inputMode = 'file_import',
  rawText = '',
  fileName = '',
  fileType = '',
  fileSize = 0,
} = {}) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const canonicalText = resolveCanonicalEntryText(row);
  const sourceText = resolveCanonicalSourceText(row, rawText, canonicalText);
  const originalText = resolveCanonicalOriginalText(row, rawText, canonicalText);
  const metadata = {
    ...(row.metadata || {}),
    canonicalRowVersion: row?.canonicalRowVersion || 1,
    parserMode: row?.parserMode || parserMode,
    sourceMode: row?.sourceMode || sourceMode,
    inputMode: row?.inputMode || inputMode,
    rawText: row?.rawText || rawText || sourceText || originalText || '',
    rawSourceText: row?.rawSourceText || rawText || sourceText || originalText || '',
  };

  if (fileName) {
    metadata.fileName = fileName;
  }

  if (fileType) {
    metadata.fileType = fileType;
  }

  if (Number.isFinite(Number(fileSize)) && Number(fileSize) > 0) {
    metadata.fileSize = Number(fileSize);
  }

  return {
    ...row,
    parserMode: row?.parserMode || parserMode,
    sourceMode: row?.sourceMode || sourceMode,
    inputMode: row?.inputMode || inputMode,
    rawText: row?.rawText || rawText || sourceText || originalText || '',
    rawSourceText: row?.rawSourceText || rawText || sourceText || originalText || '',
    metadata,
  };
};

export const normalizeImportedFileReviewRows = (rows = [], options = {}) => (
  Array.isArray(rows)
    ? resolveDominantCategoryOverlaps(rows.map((row) => normalizeImportedFileReviewRow(row, options)))
    : []
);
